import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Badge di stato salute email dell'agenzia (SMTP + IMAP) — visibile nella topbar
 * solo per l'admin. Verde = tutto OK, ambra = config parziale, rosso = errore.
 * Click porta l'utente alle impostazioni pertinenti.
 */
export function EmailHealthBadge() {
  const [smtp, setSmtp] = useState(null);
  const [imap, setImap] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [s, i] = await Promise.all([
          api.get("/agency/email-settings").catch(() => ({ data: null })),
          api.get("/agency/inbox-settings").catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setSmtp(s?.data || null);
        setImap(i?.data || null);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (!smtp && !imap) return null;

  // Stato SMTP: verde se enabled+password+verified, ambra se enabled+password ma non testato, rosso se enabled ma manca la password
  let smtpStatus = "off"; // off | ok | warn | error
  if (smtp?.smtp_enabled) {
    if (smtp.smtp_app_password_set && smtp.smtp_verified) smtpStatus = "ok";
    else if (smtp.smtp_app_password_set) smtpStatus = "warn";
    else smtpStatus = "error";
  }

  // Stato IMAP: verde se enabled+pass_set+last_polled_at senza errori, rosso se last_error, ambra se attivo ma non testato
  let imapStatus = "off";
  if (imap?.enabled) {
    if (imap.imap_pass_set && imap.last_polled_at && !imap.last_error) imapStatus = "ok";
    else if (imap.last_error) imapStatus = "error";
    else if (imap.imap_pass_set) imapStatus = "warn";
    else imapStatus = "error";
  }

  const dotClass = (st) => (
    st === "ok" ? "bg-emerald-500" :
    st === "warn" ? "bg-amber-500" :
    st === "error" ? "bg-rose-500" :
    "bg-slate-300"
  );
  const overall = [smtpStatus, imapStatus].includes("error") ? "error"
    : [smtpStatus, imapStatus].includes("warn") ? "warn"
    : [smtpStatus, imapStatus].includes("ok") ? "ok" : "off";
  const borderClass = overall === "ok" ? "hover:border-emerald-300"
    : overall === "warn" ? "hover:border-amber-300 border-amber-200"
    : overall === "error" ? "hover:border-rose-300 border-rose-200"
    : "hover:border-slate-300";
  const label = overall === "ok" ? "Email OK"
    : overall === "warn" ? "Email da testare"
    : overall === "error" ? "Email errore" : "Email off";
  const iconColor = overall === "ok" ? "text-emerald-600"
    : overall === "warn" ? "text-amber-600"
    : overall === "error" ? "text-rose-600" : "text-slate-500";

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const smtpText = smtpStatus === "ok" ? `verificato · ${fmt(smtp.smtp_verified_at)}`
    : smtpStatus === "warn" ? "config presente — non testato"
    : smtpStatus === "error" ? (smtp?.smtp_enabled ? "app password mancante" : "non configurato")
    : "disattivato";
  const imapText = imapStatus === "ok" ? `connesso · ${fmt(imap.last_polled_at)}`
    : imapStatus === "error" ? (imap?.last_error || "config incompleta")
    : imapStatus === "warn" ? "attivo — non ancora testato"
    : "disattivato";

  return (
    <div className="relative" data-testid="email-health-badge">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-sm px-3 py-1.5 transition-colors ${borderClass}`}
        title={label}
      >
        <span className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full rounded-full ${dotClass(overall)} ${overall === "ok" ? "animate-ping opacity-60" : ""}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${dotClass(overall)}`} />
        </span>
        <Mail className={`h-3.5 w-3.5 ${iconColor}`} />
        <span className="text-xs font-medium text-slate-700 hidden xl:inline">{label}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-md bg-white border border-slate-200 shadow-lg p-3 z-40 text-xs" data-testid="email-health-panel">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Stato Email Agenzia</p>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/agency/email-settings"); }}
            className="w-full text-left flex items-start gap-2 py-2 hover:bg-slate-50 rounded px-1"
            data-testid="email-health-goto-smtp"
          >
            <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${dotClass(smtpStatus)}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800">📤 SMTP (invio)</div>
              <div className="text-slate-500 truncate">{smtpText}</div>
            </div>
            <span className="text-slate-400">›</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/admin/agency"); }}
            className="w-full text-left flex items-start gap-2 py-2 hover:bg-slate-50 rounded px-1 mt-1"
            data-testid="email-health-goto-imap"
          >
            <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${dotClass(imapStatus)}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800">📥 IMAP (ricezione)</div>
              <div className="text-slate-500 truncate" title={imapText}>{imapText}</div>
            </div>
            <span className="text-slate-400">›</span>
          </button>
        </div>
      )}
    </div>
  );
}
